import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface State {
  /** True when the browser has signaled the app is installable. Click → install(). */
  canInstall: boolean;
  /** True on iOS/iPadOS Safari (which has no programmatic install API). */
  isIos: boolean;
  /** True when the app is already running as a standalone PWA. */
  installed: boolean;
  /** Fire the native install prompt. Returns `true` if accepted. */
  install: () => Promise<boolean>;
}

export function useInstallPrompt(): State {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    // Already installed?
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      // iOS Safari exposes its own legacy flag
      (window.navigator as any).standalone === true;
    setInstalled(standalone);

    // iOS detection (Safari can't programmatically prompt — show instructions instead)
    const ua = window.navigator.userAgent;
    const iosLike = /iPad|iPhone|iPod/.test(ua) ||
      // iPadOS now reports as desktop Safari with touch points > 1
      (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
    setIsIos(iosLike);

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!event) return false;
    try {
      await event.prompt();
      const choice = await event.userChoice;
      setEvent(null);
      return choice.outcome === 'accepted';
    } catch {
      return false;
    }
  };

  return { canInstall: !!event && !installed, isIos, installed, install };
}
